'use client'

import { DashboardLayout } from '@/components/dashboard-layout'
import { StatCard } from '@/components/stat-card'
import { Badge } from '@/components/badge'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { Rocket, CheckCircle2, AlertCircle, Clock, TrendingUp, Server, Zap } from 'lucide-react'

const deploymentStats = [
  { title: 'Total Deployments', value: '847', subtitle: '+42 this month', icon: Rocket },
  { title: 'Success Rate', value: '98.3%', subtitle: 'All systems nominal', icon: CheckCircle2 },
  { title: 'Avg Deploy Time', value: '3m 24s', subtitle: 'Optimized pipeline', icon: Clock },
  { title: 'Active Deployments', value: '12', subtitle: 'In progress', icon: TrendingUp },
]

const deploymentTimeline = [
  { time: '12:45', instance: 'Cowrie SSH 01', template: 'Cowrie', status: 'Completed', node: 'X-Node-NY-01', duration: '2m 15s' },
  { time: '12:30', instance: 'Dionaea Malware', template: 'Dionaea', status: 'Completed', node: 'X-Node-LN-02', duration: '3m 42s' },
  { time: '12:10', instance: 'Elasticpot Web', template: 'Elasticpot', status: 'Completed', node: 'X-Node-SG-03', duration: '1m 58s' },
  { time: '11:55', instance: 'Log4Shell Vuln', template: 'Log4Pot', status: 'Running', node: 'X-Node-NY-01', duration: '4m 23s (ongoing)' },
  { time: '11:30', instance: 'SMB Honey', template: 'SMB', status: 'Completed', node: 'X-Node-LN-02', duration: '2m 51s' },
  { time: '11:05', instance: 'Conpot ICS', template: 'Conpot', status: 'Failed', node: 'X-Node-SG-03', duration: '45s' },
]

const deploymentMetrics = [
  { name: 'Mon', successful: 34, failed: 2, running: 1 },
  { name: 'Tue', successful: 42, failed: 1, running: 2 },
  { name: 'Wed', successful: 38, failed: 3, running: 1 },
  { name: 'Thu', successful: 45, failed: 2, running: 3 },
  { name: 'Fri', successful: 52, failed: 1, running: 4 },
  { name: 'Sat', successful: 31, failed: 2, running: 2 },
  { name: 'Sun', successful: 28, failed: 1, running: 0 },
]

const deploymentsByTemplate = [
  { name: 'Cowrie', value: 245, fill: 'oklch(0.55 0.22 264)' },
  { name: 'Dionaea', value: 189, fill: 'oklch(0.65 0.18 200)' },
  { name: 'Elasticpot', value: 156, fill: 'oklch(0.72 0.16 120)' },
  { name: 'Log4Pot', value: 132, fill: 'oklch(0.4 0.15 30)' },
  { name: 'Others', value: 125, fill: 'oklch(0.5 0.2 0)' },
]

const activeDeployments = [
  {
    id: 'dep-2847',
    instance: 'Dionaea Malware 12',
    template: 'Dionaea',
    node: 'X-Node-LN-02',
    startTime: '2:34 PM',
    estimatedTime: '3m 12s',
    progress: 68,
    status: 'Pulling dependencies',
  },
  {
    id: 'dep-2846',
    instance: 'Log4Shell Vulnerable',
    template: 'Log4Pot',
    node: 'X-Node-NY-01',
    startTime: '2:31 PM',
    estimatedTime: '4m 45s',
    progress: 42,
    status: 'Container initialization',
  },
  {
    id: 'dep-2845',
    instance: 'SMB Honey 08',
    template: 'SMB',
    node: 'X-Node-SG-03',
    startTime: '2:28 PM',
    estimatedTime: '2m 30s',
    progress: 85,
    status: 'Network configuration',
  },
  {
    id: 'dep-2844',
    instance: 'Cowrie SSH 15',
    template: 'Cowrie',
    node: 'X-Node-NY-01',
    startTime: '2:25 PM',
    estimatedTime: '2m 15s',
    progress: 92,
    status: 'Finalizing setup',
  },
]

export default function DeploymentsPage() {
  return (
    <DashboardLayout
      title="Deployments"
      subtitle="Manage and monitor honeypot and infrastructure deployments"
      actions={
        <button className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity">
          New Deployment
        </button>
      }
    >
      <div className="space-y-8">
        {/* Stats Grid */}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {deploymentStats.map((stat) => (
            <StatCard
              key={stat.title}
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
            />
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Deployment Activity Chart */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Weekly Deployment Activity</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={deploymentMetrics}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0 0)" />
                <XAxis dataKey="name" stroke="oklch(0.65 0 0)" />
                <YAxis stroke="oklch(0.65 0 0)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'oklch(0.158 0 0)',
                    border: '1px solid oklch(0.25 0 0)',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'oklch(0.94 0 0)' }}
                />
                <Legend wrapperStyle={{ color: 'oklch(0.94 0 0)' }} />
                <Bar dataKey="successful" stackId="a" fill="oklch(0.72 0.16 120)" />
                <Bar dataKey="running" stackId="a" fill="oklch(0.55 0.22 264)" />
                <Bar dataKey="failed" stackId="a" fill="oklch(0.577 0.245 27.325)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Deployments by Template */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Deployments by Template</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={deploymentsByTemplate}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name} (${value})`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {deploymentsByTemplate.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'oklch(0.158 0 0)',
                    border: '1px solid oklch(0.25 0 0)',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'oklch(0.94 0 0)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Active Deployments */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Zap size={20} className="text-primary" />
              Active Deployments
            </h2>
            <span className="text-sm text-muted-foreground">{activeDeployments.length} in progress</span>
          </div>

          <div className="space-y-4">
            {activeDeployments.map((deployment) => (
              <div
                key={deployment.id}
                className="bg-secondary rounded-lg p-4 border border-border hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{deployment.instance}</h3>
                      <Badge variant="info" text={deployment.template} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Node: <span className="text-foreground">{deployment.node}</span> • Started: {deployment.startTime}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">{deployment.progress}%</p>
                    <p className="text-xs text-muted-foreground">~{deployment.estimatedTime} remaining</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-2">
                  <div className="w-full bg-border rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-300"
                      style={{ width: `${deployment.progress}%` }}
                    />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">{deployment.status}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Deployment History */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-6">Recent Deployment History</h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Time</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Instance</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Template</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Node</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
                  <th className="text-right px-4 py-3 text-muted-foreground font-medium">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {deploymentTimeline.map((item, idx) => (
                  <tr key={idx} className="hover:bg-secondary/50 transition-colors">
                    <td className="px-4 py-3 text-foreground">{item.time}</td>
                    <td className="px-4 py-3 text-foreground font-medium">{item.instance}</td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        text={item.template}
                      />
                    </td>
                    <td className="px-4 py-3 text-foreground text-xs">{item.node}</td>
                    <td className="px-4 py-3">
                      {item.status === 'Completed' && (
                        <Badge variant="success" text={item.status} />
                      )}
                      {item.status === 'Running' && (
                        <Badge variant="info" text={item.status} />
                      )}
                      {item.status === 'Failed' && (
                        <Badge variant="error" text={item.status} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground text-xs">{item.duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
